import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { fmtINR } from "../lib/format";
import { Coins, Download } from "lucide-react";

async function downloadCsv(url, filename) {
  try {
    const { data } = await api.get(url, { responseType: "blob" });
    const objUrl = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = objUrl; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
  } catch { /* ignore */ }
}

export default function CostToServe() {
  const [rep, setRep] = useState(null);
  const [tab, setTab] = useState("pop");

  useEffect(() => {
    api.get("/reports/cost-to-serve").then((r) => setRep(r.data)).catch(() => {});
  }, []);

  if (!rep) return <div className="text-slate-500">Loading…</div>;

  const Row3 = ({ a, b, c, d, bold }) => (
    <tr className={`border-b border-slate-100 ${bold ? "font-semibold" : ""}`}>
      <td className="px-4 py-3">{a}</td>
      <td className="px-4 py-3 font-mono-num text-right">{fmtINR(b)}</td>
      <td className="px-4 py-3 font-mono-num text-right text-rose-700">{fmtINR(c)}</td>
      <td className="px-4 py-3 font-mono-num text-right">{fmtINR(d)}</td>
    </tr>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-2xl font-extrabold flex items-center gap-2"><Coins className="text-emerald-600" /> Cost to Serve</h1>
          <p className="text-slate-500 text-sm mt-1">{rep.note} Finance / management only.</p>
        </div>
        <button onClick={() => downloadCsv("/reports/cost-to-serve.csv", "cost-to-serve.csv")}
          className="flex items-center gap-1.5 text-sm text-blue-700 font-medium"><Download size={15} /> Export CSV</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase text-slate-500 font-bold">Last-mile ₹/mo</div>
          <div className="font-head text-2xl font-extrabold mt-1">{fmtINR(rep.totals?.last_mile)}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase text-slate-500 font-bold">Backbone ₹/mo</div>
          <div className="font-head text-2xl font-extrabold mt-1 text-rose-700">{fmtINR(rep.totals?.backbone)}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase text-slate-500 font-bold">Total cost to serve ₹/mo</div>
          <div className="font-head text-2xl font-extrabold mt-1">{fmtINR((rep.totals?.last_mile || 0) + (rep.totals?.backbone || 0))}</div>
        </div>
      </div>

      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg text-sm w-fit">
        {[["pop", "By POP"], ["customer", "By Customer Link"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)}
            className={`px-3 py-1.5 rounded-md font-medium ${tab === v ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500">
            <th className="px-4 py-3">{tab === "pop" ? "POP" : "Customer link"}</th>
            <th className="px-4 py-3 text-right">Last-mile ₹/mo</th>
            <th className="px-4 py-3 text-right">Backbone share ₹/mo</th>
            <th className="px-4 py-3 text-right">Total cost to serve ₹/mo</th>
          </tr></thead>
          <tbody>
            {tab === "pop" && rep.by_pop.map((r, i) => (
              <Row3 key={i} a={`${r.pop_name} · ${r.links} link(s)`} b={r.last_mile_cost} c={r.backbone_share} d={r.total_cost_to_serve} />
            ))}
            {tab === "customer" && rep.by_customer.map((r, i) => (
              <Row3 key={i} a={`${r.customer || r.connection_name || r.link_id}${r.pop_name ? ` · ${r.pop_name}` : ""}`} b={r.last_mile_cost} c={r.backbone_share} d={r.total_cost_to_serve} />
            ))}
            {((tab === "pop" ? rep.by_pop : rep.by_customer).length === 0) && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400">No priced items to allocate yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
